import { MessageCircle, Reply, Send } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatDistanceToNow } from "date-fns";
import { zhCN } from "date-fns/locale";
import { type NewsCommentSortMode } from "@/lib/client/news-comment";
import type { CurrentUser } from "@/lib/client/user";
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import {
  COMMENT_SORT_OPTIONS,
  createEmptyReactionState,
  DEFAULT_REACTION_DISPLAY_COUNT,
  DEFAULT_VISIBLE_REPLY_COUNT,
  MAX_COMMENT_LENGTH,
  type CommentThreadItem,
  type ReactionSummaryMap,
} from "./news-comment-section-helpers";
import { NEWS_REACTION_EMOJIS, type NewsReactionEmoji } from "@/lib/utils/news-reaction";
import { getNewsReactionTargetKey } from "@/lib/utils/news-reaction";

interface NewsCommentSectionRendererProps {
  currentUser: CurrentUser | null;
  commentsCount: number;
  sortMode: NewsCommentSortMode;
  onSortModeChange: (mode: NewsCommentSortMode) => void;
  newCommentContent: string;
  onNewCommentChange: (content: string) => void;
  onSubmitRoot: () => void;
  isSubmittingComment: boolean;
  threads: CommentThreadItem[];
  authorNameById: Map<string, string>;
  expandedReplies: Record<string, boolean>;
  toggleExpanded: (rootId: string) => void;
  replyTo: string | null;
  setReplyTo: Dispatch<SetStateAction<string | null>>;
  replyDrafts: Record<string, string>;
  setReplyDraft: (commentId: string, content: string) => void;
  onSubmitReply: (parentId: string) => void;
  reactionMap: ReactionSummaryMap;
  onReactionSubmit: (args: {
    targetType: "news" | "comment";
    targetId: string;
    emoji: NewsReactionEmoji;
  }) => void;
  isReactionPending: boolean;
}

function getCommentTimeLabel(dateValue: Date | string, hydrated: boolean) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  if (!hydrated) {
    return date.toISOString();
  }

  return formatDistanceToNow(date, {
    locale: zhCN,
    addSuffix: true,
  });
}

function ReactionBar({
  targetType,
  targetId,
  currentUser,
  isReactionPending,
  reactionMap,
  onReactionSubmit,
}: {
  targetType: "news" | "comment";
  targetId: string;
  currentUser: CurrentUser | null;
  isReactionPending: boolean;
  reactionMap: ReactionSummaryMap;
  onReactionSubmit: (args: {
    targetType: "news" | "comment";
    targetId: string;
    emoji: NewsReactionEmoji;
  }) => void;
}) {
  const key = getNewsReactionTargetKey({ targetType, targetId });
  const bucket = reactionMap.get(key) ?? createEmptyReactionState();
  const visibleEmojis = Array.from(bucket.keys()).slice(0, DEFAULT_REACTION_DISPLAY_COUNT);
  const displayEmojis: NewsReactionEmoji[] =
    visibleEmojis.length > 0
      ? visibleEmojis
      : NEWS_REACTION_EMOJIS.slice(0, DEFAULT_REACTION_DISPLAY_COUNT);

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {displayEmojis.map((emoji) => {
        const state = bucket.get(emoji) ?? { count: 0, reacted: false };

        return (
          <button
            key={`${key}:${emoji}`}
            type="button"
            disabled={!currentUser || isReactionPending}
            className={`inline-flex min-h-7 shrink-0 items-center gap-1 rounded-full border px-2 text-xs ${
              state.reacted
                ? "border-rose-500 bg-rose-500/10 text-rose-600"
                : "border-border/80 text-muted-foreground"
            }`}
            onClick={() => {
              if (!currentUser) {
                return;
              }
              onReactionSubmit({
                targetType,
                targetId,
                emoji,
              });
            }}
          >
            <span>{emoji}</span>
            {state.count > 0 ? <span>{state.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function NewsCommentSectionRenderer({
  currentUser,
  commentsCount,
  sortMode,
  onSortModeChange,
  newCommentContent,
  onNewCommentChange,
  onSubmitRoot,
  isSubmittingComment,
  threads,
  authorNameById,
  expandedReplies,
  toggleExpanded,
  replyTo,
  setReplyTo,
  replyDrafts,
  setReplyDraft,
  onSubmitReply,
  reactionMap,
  onReactionSubmit,
  isReactionPending,
}: NewsCommentSectionRendererProps) {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold leading-none flex items-center gap-2">
          <MessageCircle className="size-4" />
          评论 {commentsCount}
        </h2>
        <div className="flex items-center gap-1.5">
          {COMMENT_SORT_OPTIONS.map((option) => {
            const isActive = sortMode === option.value;
            return (
              <Button
                key={option.value}
                size="sm"
                variant={isActive ? "secondary" : "outline"}
                className="h-7 px-2.5 text-xs"
                onClick={() => onSortModeChange(option.value)}
              >
                {option.label}
              </Button>
            );
          })}
        </div>
      </header>

      {currentUser ? (
        <div className="space-y-2">
          <Textarea
            placeholder="来说点什么吧！"
            value={newCommentContent}
            maxLength={MAX_COMMENT_LENGTH}
            onChange={(event) => onNewCommentChange(event.target.value)}
            className="min-h-24"
            rows={3}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {newCommentContent.length}/{MAX_COMMENT_LENGTH}
            </p>
            <Button
              onClick={onSubmitRoot}
              disabled={isSubmittingComment || !newCommentContent.trim()}
              size="sm"
            >
              <Send className="size-4" />
              {isSubmittingComment ? "发布中..." : "发布评论"}
            </Button>
          </div>
        </div>
      ) : (
        <p className="border border-dashed p-3 text-sm text-muted-foreground">
          登录后可参与评论与回复。
        </p>
      )}

      <ul className="divide-y divide-border/70">
        {threads.length === 0 ? (
          <li>
            <p className="py-8 text-center text-sm text-muted-foreground">暂无评论，快来抢沙发~</p>
          </li>
        ) : (
          threads.map((thread) => {
            const root = thread.root;
            const isExpanded = !!expandedReplies[root.id];
            const visibleReplies = isExpanded
              ? thread.replies
              : thread.replies.slice(0, DEFAULT_VISIBLE_REPLY_COUNT);
            const activeReplyId = replyTo && thread.commentIds.has(replyTo) ? replyTo : null;
            const activeReplyTargetName = activeReplyId
              ? authorNameById.get(activeReplyId) || "TA"
              : null;
            const rootCreatedLabel = getCommentTimeLabel(root.createdAt, isHydrated);

            return (
              <li key={root.id} className="py-4">
                <article className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Avatar className="size-9 shrink-0">
                      {thread.rootBadge.avatarUrl ? (
                        <AvatarImage src={thread.rootBadge.avatarUrl} alt={thread.rootAuthorName} />
                      ) : (
                        <AvatarFallback>{thread.rootBadge.fallback}</AvatarFallback>
                      )}
                    </Avatar>

                    <div className="min-w-0 flex-1 space-y-1.5">
                      <p className="text-sm font-semibold leading-tight break-all">
                        {thread.rootAuthorName}
                      </p>
                      <p className="text-xs text-muted-foreground" suppressHydrationWarning>
                        {rootCreatedLabel}
                      </p>
                      <p className="text-base leading-relaxed whitespace-pre-wrap break-words">
                        {root.content}
                      </p>
                      <div className="mt-1 flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <ReactionBar
                            targetType="comment"
                            targetId={root.id}
                            currentUser={currentUser}
                            isReactionPending={isReactionPending}
                            reactionMap={reactionMap}
                            onReactionSubmit={onReactionSubmit}
                          />
                        </div>
                        {currentUser ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 shrink-0 px-2 text-muted-foreground"
                            onClick={() =>
                              setReplyTo((current) => (current === root.id ? null : root.id))
                            }
                          >
                            <Reply className="size-3.5" />
                            <span>回复</span>
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {thread.replies.length > 0 ? (
                    <div className="ml-12 rounded-xl border border-border/60 bg-muted/35">
                      <ul className="space-y-2.5 p-2">
                        {visibleReplies.map((reply) => (
                          <li key={reply.id} className="text-xs leading-relaxed">
                            <p className="break-words">
                              <span className="text-xs font-medium text-sky-600 dark:text-sky-400">
                                {reply.authorName}
                              </span>
                              {reply.isRootAuthor ? (
                                <span className="ml-1 mr-1 inline-flex h-4 items-center rounded-full border border-muted-foreground/35 bg-muted/35 px-1 text-[10px] font-medium text-muted-foreground">
                                  楼主
                                </span>
                              ) : null}
                              {reply.parentName && !reply.isParentRootAuthor ? (
                                <>
                                  <span className="text-muted-foreground"> 回复 </span>
                                  <span className="font-medium text-sky-600 dark:text-sky-400">
                                    {reply.parentName}
                                  </span>
                                </>
                              ) : null}
                              <span className="text-foreground">：{reply.content}</span>
                            </p>
                            <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                              <span suppressHydrationWarning>
                                {getCommentTimeLabel(reply.createdAt, isHydrated)}
                              </span>
                              {currentUser ? (
                                <button
                                  type="button"
                                  className="hover:text-foreground"
                                  onClick={() =>
                                    setReplyTo((current) =>
                                      current === reply.id ? null : reply.id,
                                    )
                                  }
                                >
                                  回复
                                </button>
                              ) : null}
                            </div>
                          </li>
                        ))}
                      </ul>

                      {thread.replies.length > DEFAULT_VISIBLE_REPLY_COUNT ? (
                        <button
                          type="button"
                          className="mt-1 w-full border-t border-border/60 py-1 text-center text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => toggleExpanded(root.id)}
                        >
                          {isExpanded ? "收起回复" : `查看全部 ${thread.replies.length} 条回复`}
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  {currentUser && activeReplyId ? (
                    <div className="ml-12 space-y-2">
                      <p className="text-xs text-muted-foreground">回复 @{activeReplyTargetName}</p>
                      <Textarea
                        value={replyDrafts[activeReplyId] ?? ""}
                        maxLength={MAX_COMMENT_LENGTH}
                        onChange={(event) => setReplyDraft(activeReplyId, event.target.value)}
                        rows={2}
                        placeholder="写下你的回复..."
                        className="min-h-20"
                      />
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {(replyDrafts[activeReplyId] ?? "").length}/{MAX_COMMENT_LENGTH}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setReplyTo(null)}
                            disabled={isSubmittingComment}
                          >
                            取消
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => onSubmitReply(activeReplyId)}
                            disabled={
                              isSubmittingComment ||
                              (replyDrafts[activeReplyId] ?? "").trim().length === 0
                            }
                          >
                            {isSubmittingComment ? "提交中..." : "回复"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </article>
              </li>
            );
          })
        )}
      </ul>
    </section>
  );
}
